<template>
	<div
		class="help-composer"
		data-testid="help-assistant-composer"
	>
		<!--
			Mirrors the React HelpAssistant composer (AB#27211): one bordered group holding the field
			and its Send, which becomes Stop while an answer streams. The field itself stays live
			while streaming, so the next question can be drafted as the answer arrives; only sending
			waits (handleSend guards on `streaming`). Hand-rolled rather than built on
			`v-textarea`/`v-btn`, because Vuetify forces its own fieldset chrome, a 64px minimum button
			width and uppercase labels, none of which the React composer has. Keep the two in step.
		-->
		<div
			class="help-composer__group"
			role="group"
			@click="focus"
		>
			<textarea
				:id="inputId"
				ref="input"
				v-model="text"
				aria-label="Ask a question"
				data-testid="help-assistant-input"
				class="help-composer__field"
				rows="1"
				:maxlength="MAX_MESSAGE_CHARS"
				:disabled="disabled"
				:placeholder="placeholder"
				@keydown="handleKeydown"
				@input="resize"
			/>

			<span class="help-composer__addon">
				<button
					v-if="streaming"
					type="button"
					data-testid="help-assistant-stop-button"
					aria-label="Stop generating"
					class="help-composer__stop"
					@click.stop="handleStop"
				>
					<LucideIcon
						name="Square"
						:size="14"
						class="help-composer__stop-icon"
					/>
					Stop
				</button>
				<button
					v-else
					type="button"
					data-testid="help-assistant-send-button"
					aria-label="Send"
					:disabled="isSendDisabled"
					class="help-composer__send"
					@click.stop="handleSend"
				>
					<LucideIcon
						name="ArrowUp"
						:size="15"
					/>
				</button>
			</span>
		</div>

		<!--
			Under the field, silent until 200 characters of headroom remain: the field's maxlength cuts
			a long paste short, and without this nothing said so. At the limit the words carry the
			meaning rather than a colour: 12px red misses the 4.5:1 contrast floor, and colour alone is
			not a signal. The status region is always in the tree, so its two changes (appearing, and
			the limit) announce; the count itself is not live, or every keystroke near the limit would
			be. Mirrors the React composer.
		-->
		<p
			v-if="counter"
			data-testid="help-assistant-char-count"
			:class="['help-composer__count', { 'help-composer__count--limit': counter.atLimit }]"
		>
			{{ counter.text }}{{ counter.atLimit ? ' · Limit reached' : '' }}
		</p>
		<span
			role="status"
			data-testid="help-assistant-char-count-status"
			class="d-sr-only"
		>
			{{ counter ? counter.status : '' }}
		</span>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { COMPOSER_MAX_HEIGHT_PX, MAX_MESSAGE_CHARS, composerCounter } from './utils/chat-core.js'
import { HELP_ASSISTANT_COMPOSER_PLACEHOLDER } from './utils/assistant-copy.js'

export default {
	name: 'help-composer',
	components: {
		LucideIcon
	},
	props: {
		disabled: {
			type: Boolean,
			default: false
		},
		streaming: {
			type: Boolean,
			default: false
		},
		// Mirrors the React HelpAssistant's `composerPlaceholder`.
		placeholder: {
			type: String,
			default: HELP_ASSISTANT_COMPOSER_PLACEHOLDER
		}
	},
	data () {
		return {
			text: '',
			inputId: `help-assistant-input-${this._uid}`,
			MAX_MESSAGE_CHARS
		}
	},
	computed: {
		isSendDisabled () {
			return this.text.trim().length === 0 || this.disabled
		},
		counter () {
			return composerCounter(this.text.length)
		}
	},
	mounted () {
		this.resize()
	},
	methods: {
		/** Lets a host put the caret here. The panel's `focusComposer()` calls this, which the splash
		 * hero uses when a conversation opens or is switched. preventScroll: this field sits at the
		 * bottom of the panel, and the default focus behaviour scrolls it into view, dragging the
		 * page while the host is mid-transition (AB#27211). */
		focus () {
			this.$refs.input?.focus({ preventScroll: true })
		},
		/** Grows with the content to the cap, then scrolls. Reset first so it can shrink again. The
		 * cap is set here too, so the stylesheet has no second copy of the number to drift. */
		resize () {
			const field = this.$refs.input
			if (!field) return
			field.style.maxHeight = `${COMPOSER_MAX_HEIGHT_PX}px`
			field.style.height = 'auto'
			const next = field.scrollHeight
			if (next <= 0) return
			field.style.height = `${Math.min(next, COMPOSER_MAX_HEIGHT_PX)}px`
			field.style.overflowY = next > COMPOSER_MAX_HEIGHT_PX ? 'auto' : 'hidden'
		},
		handleKeydown (event) {
			// isComposing: committing an IME candidate (Japanese, Chinese, Korean) fires Enter, and
			// without this the draft sends mid-composition. The React composer guards the same way.
			if (event.isComposing) return
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault()
				this.handleSend()
			}
		},
		handleSend () {
			const trimmed = this.text.trim()
			if (!trimmed || this.disabled || this.streaming) return
			this.$emit('send', { text: trimmed })
			this.text = ''
			this.$nextTick(this.resize)
		},
		handleStop () {
			this.$emit('stop')
		}
	}
}
</script>

<style lang="scss" scoped>
// Sizes match the React composer: 13px text on an 18px line, growth capped by resize(), a 32px control
// inset 6px inside the group.
.help-composer {
	padding: 12px;
	border-top: 1px solid var(--border, #E0E2E5);

	// flex-end: Send sits on the last line of a multi-line draft, beside the caret, as every
	// mainstream composer places it. The field's 36px single-line height and the addon's 2px bottom
	// inset keep a one-line composer exactly where centring put it. Mirrors the React composer.
	&__group {
		display: flex;
		align-items: flex-end;
		width: 100%;
		padding: 6px 0;
		cursor: text;
		background-color: var(--card, #FFFFFF);
		border: 1px solid var(--input, #E0E2E5);
		border-radius: var(--radius, 8px);
		transition: border-color 0.15s ease, box-shadow 0.15s ease;

		&:focus-within {
			border-color: var(--primary, #406A94);
			box-shadow: 0 0 0 3px rgb(64 106 148 / 25%);
		}
	}

	&__field {
		flex: 1 1 auto;
		min-width: 0;
		min-height: 36px;
		padding: 8px 8px 8px 14px;
		overflow-y: hidden;
		font-family: inherit;
		font-size: 13px;
		line-height: 18px;
		color: var(--foreground, #4A5568);
		resize: none;
		background: none;
		border: none;
		outline: none;

		&::placeholder {
			color: var(--muted-foreground, #666E7A);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}
	}

	&__addon {
		display: flex;
		flex: none;
		align-items: center;
		padding-right: 6px;
		padding-bottom: 2px;
	}

	&__count {
		margin: 4px 0 0;
		font-size: 0.75rem;
		font-variant-numeric: tabular-nums;
		color: var(--muted-foreground, #666E7A);
		text-align: right;

		&--limit {
			font-weight: 500;
			color: var(--foreground, #4A5568);
		}
	}

	&__send {
		display: flex;
		flex: none;
		align-items: center;
		justify-content: center;
		width: 32px;
		min-width: 32px;
		height: 32px;
		padding: 0;
		color: var(--primary-foreground, #FFFFFF);
		cursor: pointer;
		background-color: var(--primary, #406A94);
		border: none;
		border-radius: var(--radius, 8px);

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--primary, #406A94);
			outline-offset: 2px;
		}
	}

	// React's Stop is an outline button: 32px tall, sentence case, with a filled square glyph.
	// Vuetify's would have rendered it uppercase and at least 64px wide.
	&__stop {
		display: flex;
		flex: none;
		gap: 6px;
		align-items: center;
		height: 32px;
		padding: 0 10px;
		font-family: inherit;
		font-size: 14px;
		font-weight: 500;
		line-height: 1;
		color: var(--foreground, #4A5568);
		text-transform: none;
		letter-spacing: normal;
		cursor: pointer;
		background-color: var(--card, #FFFFFF);
		border: 1px solid var(--input, #E0E2E5);
		border-radius: var(--radius, 8px);
		transition: background-color 0.15s ease;

		&:hover {
			background-color: var(--muted, #F5F5F5);
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--primary, #406A94);
			outline-offset: 2px;
		}
	}

	&__stop-icon {
		fill: currentcolor;
	}
}
</style>
